'use client'

import { twMerge } from '@/lib/classname/twMerge'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { Illustration } from '@/ui/illustrations/illustration/Illustration'
import { Title } from '@/ui/text/title/Title'
import { cva } from 'class-variance-authority'
import { mergeProps, useHover, usePress } from 'react-aria'
import { toLoyaltyProgram } from '../../services/loyalty.routing'
import { cardBackgroundCva } from './_components/LoyaltyBackground'
import { LoyaltyCardTag } from './_components/LoyaltyCardTag'
import { LoyaltyBackgroundImage, LoyaltyGradient } from './_components/LoyaltyImage'
import programCardsData from './data/program-cards.json'
import { LoyaltyBaseCardSchema } from './schemas/LoyaltyCard'
import { LoyaltyTextTint } from './schemas/LoyaltyTint'

import { Link } from '@/ui/links/link/Link.client'

export type ProgramCardTypes = keyof typeof programCardsData

const cardTextCva = cva('', {
  variants: {
    color: LoyaltyTextTint,
  },
  defaultVariants: {
    color: 'white',
  },
})

export function LoyaltyProgramCard({
  type,
  loyaltyOptIn,
}: {
  type: ProgramCardTypes
  loyaltyOptIn?: boolean
}) {
  const { t } = useTranslation(['dashboard'])

  const data = LoyaltyBaseCardSchema.parse(programCardsData[type])

  const containerClasses = 'flex w-full max-w-[18.25rem] min-w-[18.25rem] flex-col gap-4'

  const { isHovered, hoverProps } = useHover({})
  const { isPressed, pressProps } = usePress({})
  const mergedProps = mergeProps(hoverProps, pressProps)

  const component = (
    <div
      className={twMerge(
        'relative flex h-15 overflow-hidden rounded-sm px-4 py-1 text-white',
        cardBackgroundCva({ tint: data.tint }),
      )}
    >
      <LoyaltyBackgroundImage
        imageSrc={data.image}
        alt={data.title?.light?.text || data.title?.big?.text}
        {...{
          className: twMerge(
            'transition-transform duration-700 ease-out',
            'group-data-[hovered]/loyalty-program-card:scale-110',
            'group-data-[pressed]/loyalty-program-card:!duration-100',
            'group-data-[pressed]/loyalty-program-card:!scale-105',
            'right-0',
          ),
        }}
      />
      <LoyaltyGradient gradient={data.gradient} />

      <div className="relative flex flex-col items-start gap-1 self-center">
        {data.tag?.programName && (
          <Illustration name="loyalty-prolians-tag" className="h-4 fill-white" />
        )}

        <LoyaltyCardTag tagLabel={data.tag?.title} color={data.tag?.color} />

        <div className="flex flex-col">
          <Title
            size="lg"
            as="h3"
            className={twMerge(
              'font-medium',
              data.title?.light?.noUppercase ? 'normal-case' : 'uppercase',
              cardTextCva({ color: data.title?.light?.color }),
            )}
          >
            {data.title?.light?.text}
          </Title>

          <Title
            size={data.title?.big?.size ?? '4xl'}
            as="h3"
            className={twMerge(
              'font-bold',
              data.title?.big?.noUppercase ? 'normal-case' : 'uppercase',
              cardTextCva({ color: data.title?.big?.color }),
            )}
          >
            {data.title?.big?.text}
          </Title>
        </div>
      </div>
    </div>
  )

  const linkUrl = loyaltyOptIn ? data.link?.url : toLoyaltyProgram
  const linkTitle = data.link?.title || t('dashboard:routing.labels.loyaltyProgram')

  return linkUrl ? (
    <Link
      variant="free"
      underlined="hover"
      href={linkUrl}
      {...mergedProps}
      data-hovered={isHovered || undefined}
      data-pressed={isPressed || undefined}
      className={twMerge(containerClasses, 'group/loyalty-program-card')}
      title={linkTitle}
    >
      {component}
    </Link>
  ) : (
    <div className={containerClasses}>{component}</div>
  )
}
